import {Suspense, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import {useSuiContext} from '@s-ui/react-context'
import PrimitiveTypography, {
  primitiveTypographyDesign,
  primitiveTypographyFontWeight,
  primitiveTypographyLineHeight
} from '@s-ui/react-primitive-typography'
import NetChatMessageList from '@adv-mt/mt-net-message-list'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import NetChatRestoringSkeleton from '../../components/NetChatRestoringSkeleton.js'
import {BLOCK_TYPES, MESSAGE_ROLES} from '../../config.js'

const BASE_CLASS = 'mt-NetDrawer-chat'

const NetChat = ({
  messages = [],
  chatId,
  onNewChat,
  onReviewSuccess,
  onShowResults,
  onAdClick,
  onSearchSaved,
  setIsVisibleLoginModal,
  onSubmit,
  onRetry,
  restoring,
  retrying,
  onFavoriteChange,
  initialSuggestions,
  headerText,
  onClickContact
}) => {
  const {domain, i18n} = useSuiContext()
  const {track} = useAnalyticsTrack()

  const [userName, setUserName] = useState('')

  const config = domain.get('config')
  const {MESSAGE_TYPES} = config.get('COMMON').AI_ASSISTANT
  const {TRACKING_EVENT_NAMES: {NET_ASSISTANT_WELCOME_CTA_CLICKED} = {}} = config.get('tracking') || {}

  const getUserDataName = async () => {
    const [error, userData] = await domain.get('get_user_by_id_user_use_case').execute()

    if (!error) {
      setUserName(userData.name)
    }
  }

  useEffect(() => {
    getUserDataName()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasConversation = messages.length > 0

  const buttonProps = {
    design: atomButtonDesigns.OUTLINE,
    shape: atomButtonShapes.CIRCULAR,
    isFitted: true,
    onClick: event => {
      const text = event?.target?.textContent

      track({event: NET_ASSISTANT_WELCOME_CTA_CLICKED, data: {hit_information: text}})
      onSubmit(text, MESSAGE_TYPES.PREDEFINED)
    }
  }

  return (
    <div className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-titleHeader`}>
        <PrimitiveTypography
          className={`${BASE_CLASS}-userName`}
          design={primitiveTypographyDesign.HEADLINE_1}
          fontWeight={primitiveTypographyFontWeight.MEDIUM}
          lineHeight={primitiveTypographyLineHeight.M}
        >
          {i18n.t('NET.WELCOME.USER', {name: userName})}
        </PrimitiveTypography>
        <PrimitiveTypography
          className={`${BASE_CLASS}-title`}
          design={primitiveTypographyDesign.DISPLAY_2}
          lineHeight={primitiveTypographyLineHeight.M}
        >
          {i18n.t('NET.WELCOME.TITLE')}
        </PrimitiveTypography>
        {!hasConversation && (
          <PrimitiveTypography design={primitiveTypographyDesign.BODY_1}>
            {headerText || i18n.t('NET.WELCOME.TEXT')}
          </PrimitiveTypography>
        )}
      </div>

      {!hasConversation && restoring && <NetChatRestoringSkeleton />}

      {!hasConversation && !restoring && (
        <div className={`${BASE_CLASS}-buttons`}>
          {initialSuggestions ? (
            initialSuggestions.map(({text}, index) => (
              <AtomButton key={`${index}-${text}`} {...buttonProps}>
                <span>{text}</span>
              </AtomButton>
            ))
          ) : (
            <>
              <AtomButton {...buttonProps}>
                <span>{i18n.t('NET.WELCOME.CTA.OPTION_ONE')}</span>
              </AtomButton>
              <AtomButton {...buttonProps}>
                <span>{i18n.t('NET.WELCOME.CTA.OPTION_TWO')}</span>
              </AtomButton>
              <AtomButton {...buttonProps}>
                <span>{i18n.t('NET.WELCOME.CTA.OPTION_THREE')}</span>
              </AtomButton>
            </>
          )}
        </div>
      )}

      {hasConversation && (
        <Suspense fallback={null}>
          <NetChatMessageList
            messages={messages}
            chatId={chatId}
            onNewChat={onNewChat}
            onReviewSuccess={onReviewSuccess}
            onShowResults={onShowResults}
            onAdClick={onAdClick}
            onSearchSaved={onSearchSaved}
            setIsVisibleLoginModal={setIsVisibleLoginModal}
            onRetry={onRetry}
            retrying={retrying}
            restoring={restoring}
            onFavoriteChange={onFavoriteChange}
            onClickContact={onClickContact}
          />
        </Suspense>
      )}
    </div>
  )
}

NetChat.displayName = 'NetChat'
NetChat.propTypes = {
  chatId: PropTypes.string,
  onNewChat: PropTypes.func,
  onReviewSuccess: PropTypes.func,
  onShowResults: PropTypes.func,
  onAdClick: PropTypes.func,
  onSearchSaved: PropTypes.func,
  setIsVisibleLoginModal: PropTypes.func,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.oneOf(Object.values(MESSAGE_ROLES)),
      text: PropTypes.string,
      blocks: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.oneOf(Object.values(BLOCK_TYPES)),
          content: PropTypes.string,
          filters: PropTypes.object,
          response: PropTypes.shape({
            meta: PropTypes.shape({
              totalResults: PropTypes.number,
              page: PropTypes.number,
              size: PropTypes.number
            }),
            items: PropTypes.array
          })
        })
      ),
      loading: PropTypes.bool
    })
  ),
  onSubmit: PropTypes.func,
  onRetry: PropTypes.func,
  restoring: PropTypes.bool,
  retrying: PropTypes.bool,
  onFavoriteChange: PropTypes.func,
  initialSuggestions: PropTypes.arrayOf(PropTypes.shape({text: PropTypes.string})),
  headerText: PropTypes.string,
  onClickContact: PropTypes.func
}

export default NetChat
